// Define constants
export const DRAWING_CONSTANTS = {
    baseWidthMm: 250,
    baseHeightMm: 200,
    dimensionMultiplier: 2,
    defaultMarginLeft: 75,
    defaultLayerDepth: 3,
    defaultInsulationDepth: 7.5,
    defaultBackgroundColor: "white",
    defaultColor: "#FFFFFF",
    sponningTypes: {
        outer: "SP_BU", // Buitenlaag (outer layer)
        inner: "SP_BI", // Binnenlaag (inner layer)
    },
    layerNames: {
        outerLayer: "buitenlaag",
        innerLayer: "binnenlaag", 
        corePlate: "kernplaat",
        insulation1: "isolatie-1",
        insulation2: "isolatie-2",
        insulation1Part2: "isolatie-1-2",
        insulation2Part2: "isolatie-2-2",
    },
    emptyValues: [0, "", null, undefined],
};

// Validation utilities
export const isEmpty = (value) => {
    return DRAWING_CONSTANTS.emptyValues.includes(value);
};

export const isValidMaterial = (value) => {
    return value && !isEmpty(value) && parseInt(value) !== 0;
};

// Visibility logic utilities
export const shouldShowPart = ({
    isolation,
    onlyKernplaat,
    onlyMaterial,
    onlyArticle,
    article,
    material
}) => {
    // Isolation with core plate only - special case
    if (isolation && onlyKernplaat) {
        return (
            isValidMaterial(onlyMaterial) &&
            isValidMaterial(onlyArticle) &&
            article !== null
        );
    }

    // Regular isolation layer
    if (isolation) {
        if (onlyArticle && article) {
            return Boolean(onlyArticle && article);
        }
        return article !== undefined && article !== null;
    }

    // Non-isolation parts (outer, inner, core layers)
    return isValidMaterial(material) && isValidMaterial(article);
};

// Sponning calculation utilities
export const determineSponningType = (partName, sponning, properties) => {
    if (partName === DRAWING_CONSTANTS.layerNames.outerLayer && 
        properties.sponning_rondom === DRAWING_CONSTANTS.sponningTypes.inner) {
        return DRAWING_CONSTANTS.sponningTypes.outer;
    }
    
    if (
        (partName === DRAWING_CONSTANTS.layerNames.corePlate || sponning === DRAWING_CONSTANTS.sponningTypes.outer) &&
        (properties.IS2 === "0" || properties.IS2 === 0) &&
        properties.sponning_rondom === DRAWING_CONSTANTS.sponningTypes.inner
    ) {
        return DRAWING_CONSTANTS.sponningTypes.inner;
    }
    
    return sponning;
};

export const extractSponningProperties = (properties, sponningType) => {
    return Object.keys(properties)
        .filter(key => key.indexOf(sponningType) === 0)
        .reduce((result, key) => {
            result[key] = properties[key];
            return result;
        }, {});
};

export const shouldBeSmallPart = ({
    isolation,
    isolation1,
    isolation2,
    partName,
    sponning,
    properties
}) => {
    // Isolation2 is always small if it exists
    if (isolation2) {
        return true;
    }
    
    // Special logic for specific configurations
    if (
        (partName === DRAWING_CONSTANTS.layerNames.corePlate || sponning === DRAWING_CONSTANTS.sponningTypes.outer) &&
        (properties.IS2 === "0" || properties.IS2 === 0) &&
        properties.sponning_rondom === DRAWING_CONSTANTS.sponningTypes.inner
    ) {
        if (isolation1) {
            return properties.sponning_rondom === DRAWING_CONSTANTS.sponningTypes.outer;
        }
        if (partName === DRAWING_CONSTANTS.layerNames.corePlate) {
            return true;
        }
        if (isolation2) {
            return properties.sponning_rondom === DRAWING_CONSTANTS.sponningTypes.inner;
        }
    }
    
    return false;
};

export const calculateSponningDimensions = (properties, product, sponning, insideView, singleIsolation, frontWhenSingleIsolation, isolation, isolation1, isolation2, partName) => {
    // Determine actual sponning type
    const sponningType = determineSponningType(partName, sponning, properties);
    
    // Extract relevant sponning properties
    const sponningProps = extractSponningProperties(properties, sponningType);
    
    // Calculate total sponning dimensions
    const totalWidth = (sponningProps[sponningType + "_LI_BR"] || 0) + (sponningProps[sponningType + "_RE_BR"] || 0);
    const totalHeight = (sponningProps[sponningType + "_O_BR"] || 0) + (sponningProps[sponningType + "_BO_BR"] || 0);
    
    // Determine if part should be small
    const isSmall = shouldBeSmallPart({
        isolation,
        isolation1,
        isolation2,
        partName,
        sponning,
        properties
    });
    
    // Calculate final dimensions
    const blockWidth = (isSmall || !isolation) 
        ? (DRAWING_CONSTANTS.baseWidthMm - totalWidth) * DRAWING_CONSTANTS.dimensionMultiplier 
        : DRAWING_CONSTANTS.baseWidthMm * DRAWING_CONSTANTS.dimensionMultiplier;
        
    const blockHeight = (isSmall || !isolation) 
        ? (DRAWING_CONSTANTS.baseHeightMm - totalHeight) * DRAWING_CONSTANTS.dimensionMultiplier 
        : DRAWING_CONSTANTS.baseHeightMm * DRAWING_CONSTANTS.dimensionMultiplier;
    
    // Calculate margins
    const marginLeft = DRAWING_CONSTANTS.defaultMarginLeft - 
        (isSmall || !isolation ? (sponningProps[sponningType + "_LI_BR"] || 0) * DRAWING_CONSTANTS.dimensionMultiplier : 0);
    const marginTop = isSmall || !isolation ? (sponningProps[sponningType + "_BO_BR"] || 0) * DRAWING_CONSTANTS.dimensionMultiplier : 0;
    
    return {
        blockWidth,
        blockHeight,
        marginLeft,
        marginTop,
    };
};

// Styling utilities
export const getBackgroundColor = (colors, color) => {
    if (colors && color !== "" && color !== "0") {
        return color;
    }
    return DRAWING_CONSTANTS.defaultBackgroundColor;
};

export const calculateShortIsolation = ({
    isolation1,
    isolation2,
    sponning,
    properties
}) => {
    if (
        isolation2 &&
        properties["sponning_boven"] === sponning &&
        properties.toon_afwerking
    ) {
        return "shortisolation small";
    }
    
    if (
        isolation1 &&
        sponning === DRAWING_CONSTANTS.sponningTypes.outer &&
        (properties.IS2 === "0" || properties.IS2 === 0) &&
        properties.sponning_rondom === DRAWING_CONSTANTS.sponningTypes.inner
    ) {
        return "shortisolation small";
    }
    
    return "";
};

// Drawing component utilities
export const calculateIsolationThickness = (properties, sessionProduct, viewSide) => {
    let notIsolationThickness = 0;
    
    // Subtract outer layer thickness if viewing from outside
    if (viewSide === DRAWING_CONSTANTS.sponningTypes.outer && sessionProduct.products.BU) {
        notIsolationThickness += sessionProduct.products.BU.zdimsize;
    }
    
    // Subtract inner layer thickness if viewing from inside
    if (viewSide === DRAWING_CONSTANTS.sponningTypes.inner && sessionProduct.products.BI) {
        notIsolationThickness += sessionProduct.products.BI.zdimsize;
    }
    
    return properties.tickness - notIsolationThickness;
};

export const calculateSponningHeight = (zDimSize, side, front, properties, sessionProduct, partName = "") => {
    // Use isolation thickness if total thickness is available
    if (properties.tickness) {
        zDimSize = calculateIsolationThickness(properties, sessionProduct, properties.sponning_rondom);
    }
    
    // Apply sponning calculations if edge finishing is enabled and uniform
    if (properties.toon_afwerking && properties.randen_afwerking_gelijk) {
        const height = properties.sponning_rondom_hoogte;
        const viewSide = properties.sponning_rondom;
        
        if (viewSide === DRAWING_CONSTANTS.sponningTypes.inner) {
            // Inside view sponning logic
            if (
                side === DRAWING_CONSTANTS.sponningTypes.outer &&
                (properties.IS2 === "0" || properties.IS2 === 0) &&
                properties.sponning_rondom === DRAWING_CONSTANTS.sponningTypes.inner
            ) {
                return front ? zDimSize - height : height;
            } else {
                return front ? height : zDimSize - height;
            }
        } else if (viewSide === DRAWING_CONSTANTS.sponningTypes.outer && side === DRAWING_CONSTANTS.sponningTypes.outer) {
            // Outside view sponning logic
            return !front ? height : zDimSize - height;
        }
    }
    
    // Default: split layer in half
    return zDimSize / 2;
};